import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/auth";
import { Input } from "antd";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { usePropostaForm } from "../context/PropostaFormContext";
import {
  axisOptions,
  getAxisFormValue,
  getDescricaoOrigemLead,
  origemLeadOptions,
  parseAxis,
} from "../utils/propostaConstants";
import PropostaSectionCard from "../components/PropostaSectionCard";
import SearchableList from "../components/SearchableList";
import PropostaSelect from "../components/PropostaSelect";
import TipoPropostaSelector from "../components/TipoPropostaSelector";
import { usePropostaToast } from "../hooks/usePropostaToast";

const { TextArea } = Input;

type SellerOption = {
  id: number;
  nome: string;
  creci: string;
  foto_perfil: string;
};

export default function PropostaResumo({
  onSave,
  onSend,
  onDownloadPdf,
  isSubmitting,
  isDownloadingPdf = false,
}: {
  onSave: () => void;
  onSend: () => void;
  onDownloadPdf?: () => void;
  isSubmitting: boolean;
  isDownloadingPdf?: boolean;
}) {
  const { api } = useAuth();
  const {
    proposta,
    setProposta,
    isReadOnly,
    setActiveTab,
    addCorretor,
    removeCorretor,
    isDark,
  } = usePropostaForm();

  const [sellerSearch, setSellerSearch] = useState("");
  const [sellerOptions, setSellerOptions] = useState<SellerOption[]>([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [showSellerList, setShowSellerList] = useState(false);
  const toast = usePropostaToast();
  const sellerSearchRef = useRef<HTMLDivElement>(null);

  useClickOutside(
    sellerSearchRef,
    () => setShowSellerList(false),
    showSellerList,
  );

  const fetchSellers = useCallback(async (search: string) => {
    setLoadingSellers(true);
    try {
      const res = await api.get(
        `/portal/seller/select?search=${encodeURIComponent(search)}`,
      );
      const data = res.data?.data ?? res.data ?? [];
      setSellerOptions(
        Array.isArray(data)
          ? data.map(
              (s: {
                id_corret_corretor: number;
                apelido: string;
                creci: string;
                foto_perfil: string;
              }) => ({
                id: s.id_corret_corretor,
                nome: s.apelido,
                creci: s.creci,
                foto_perfil: s.foto_perfil || "",
              }),
            )
          : [],
      );
    } catch {
      setSellerOptions([]);
    } finally {
      setLoadingSellers(false);
    }
  }, [api]);

  useEffect(() => {
    const timer = setTimeout(() => fetchSellers(sellerSearch), 250);
    return () => clearTimeout(timer);
  }, [sellerSearch, fetchSellers]);

  useEffect(() => {
    fetchSellers("");
  }, [fetchSellers]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryCard
          title="Proponentes"
          subtitle={
            proposta.proponentes?.length
              ? proposta.proponentes
                  .map((p) => p.nome)
                  .filter(Boolean)
                  .join(", ")
              : "Nenhum proponente cadastrado"
          }
          onClick={() => setActiveTab("proponentes")}
          isDark={isDark}
        />
        <SummaryCard
          title="Imóvel"
          subtitle={proposta.imovel?.descricao_imovel || "Nenhum imóvel"}
          onClick={() => setActiveTab("imovel")}
          isDark={isDark}
        />
        <SummaryCard
          title="Pagamentos"
          subtitle={
            proposta.pagamentos?.length
              ? `${proposta.pagamentos.length} forma(s) de pagamento`
              : "Nenhum pagamento"
          }
          onClick={() => setActiveTab("pagamentos")}
          isDark={isDark}
        />
      </div>

      <PropostaSectionCard title="Informações Gerais">
        <div className="grid grid-cols-1 gap-4">
          <Field label="Origem do Lead *" isDark={isDark}>
            <PropostaSelect
              className="w-full"
              placeholder="Selecione a origem"
              disabled={isReadOnly}
              value={proposta.id_origem_lead || undefined}
              onChange={(value) =>
                setProposta((prev) => ({
                  ...prev,
                  id_origem_lead: value,
                  descricao_origem_lead: getDescricaoOrigemLead(value),
                }))
              }
              options={origemLeadOptions}
            />
          </Field>
          <Field label="Tipo de Proposta *" isDark={isDark}>
            <TipoPropostaSelector
              disabled={isReadOnly}
              value={proposta.tipo_proposta}
              onChange={(value) =>
                setProposta((prev) => ({
                  ...prev,
                  tipo_proposta: value,
                }))
              }
            />
          </Field>
          <Field label="Proposta AXIS? *" isDark={isDark}>
            <TipoPropostaSelector
              disabled={isReadOnly}
              value={getAxisFormValue(proposta.axis)}
              options={axisOptions}
              ariaLabel="Proposta AXIS"
              columns={2}
              onChange={(value) =>
                setProposta((prev) => ({
                  ...prev,
                  axis: parseAxis(value),
                }))
              }
            />
          </Field>
          <Field label="Observações" isDark={isDark}>
            <TextArea
              rows={3}
              disabled={isReadOnly}
              value={proposta.observacao}
              onChange={(e) =>
                setProposta((prev) => ({ ...prev, observacao: e.target.value }))
              }
            />
          </Field>
        </div>
      </PropostaSectionCard>

      <PropostaSectionCard title="Corretores Relacionados">
        {proposta.corretores?.map((c) => (
          <div
            key={c.id}
            className={`flex items-center gap-3 py-2 px-2 mb-1 rounded-lg ${
              isDark ? "bg-gray-800" : "bg-gray-50"
            }`}
          >
            {c.foto_perfil && (
              <img src={c.foto_perfil}
                alt=""
                width={32}
                height={32}
                className="w-8 h-8 rounded-full object-cover" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{c.nome}</p>
              <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                CRECI: {c.creci || "Não informado"}
              </p>
            </div>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => removeCorretor(c.id)}
                className="w-fit p-0 text-red-500 text-xs font-medium hover:underline shrink-0"
              >
                Remover
              </button>
            )}
          </div>
        ))}

        {!isReadOnly && (
          <div ref={sellerSearchRef} className="mt-3 space-y-2 min-w-0 w-full">
            <Input
              placeholder="Pesquisar corretor..."
              value={sellerSearch}
              onChange={(e) => {
                setSellerSearch(e.target.value);
                setShowSellerList(true);
              }}
              onFocus={() => setShowSellerList(true)}
            />
            {showSellerList && (
              <SearchableList
                items={sellerOptions}
                loading={loadingSellers}
                getKey={(s) => s.id}
                renderItem={(s) => (
                  <button
                    type="button"
                    className={`w-full min-w-0 text-left px-3 py-2.5 flex items-center gap-3 ${
                      isDark ? "hover:bg-gray-800" : "hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      const exists = proposta.corretores?.some(
                        (c) => c.id === s.id,
                      );
                      if (exists) {
                        toast.info("Este corretor já foi adicionado.");
                        return;
                      }
                      addCorretor(s);
                      setSellerSearch("");
                      setShowSellerList(false);
                    }}
                  >
                    <img src={
                        s.foto_perfil ||
                        "https://portal-nogueira.s3.us-east-2.amazonaws.com/26848b2b91cbd520d366-placeholder-image-nogueira.png"
                      }
                      alt=""
                      width={32}
                      height={32}
                      className="w-8 h-8 rounded-full object-cover shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{s.nome}</p>
                      <p
                        className={`text-xs truncate ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        CRECI: {s.creci || "Não informado"}
                      </p>
                    </div>
                  </button>
                )}
              />
            )}
          </div>
        )}
      </PropostaSectionCard>

      <div className="flex flex-wrap gap-3 justify-end pt-2">
        {onDownloadPdf && proposta.uuid && (
          <button
            type="button"
            onClick={onDownloadPdf}
            disabled={isDownloadingPdf}
              className="px-6 py-2 rounded-md border border-primary text-primary font-semibold text-sm hover:bg-primary/10 disabled:opacity-60"
          >
            {isDownloadingPdf ? "Baixando..." : "Baixar PDF"}
          </button>
        )}
        {!isReadOnly && (
          <div className="w-full flex gap-3">
            <button
              type="button"
              onClick={onSave}
              disabled={isSubmitting}
                className="px-6 py-2 rounded-md border border-primary text-primary font-semibold text-sm hover:bg-primary/10 disabled:opacity-60"
            >
              {isSubmitting ? "Salvando..." : "Salvar"}
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={isSubmitting}
              className="px-6 py-2 rounded-md bg-primary text-white font-semibold text-sm hover:bg-primary-dark disabled:opacity-60"
            >
              {isSubmitting ? "Enviando..." : "Enviar Proposta"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  title,
  subtitle,
  onClick,
  isDark,
}: {
  title: string;
  subtitle: string;
  onClick: () => void;
  isDark: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-4 rounded-lg border hover:border-primary transition group ${
        isDark
          ? "border-gray-700 bg-[#1d2840]"
          : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-semibold text-primary">{title}</span>
        <span className="text-primary opacity-0 group-hover:opacity-100 transition text-xs">
          →
        </span>
      </div>
      <p
        className={`text-xs truncate ${
          isDark ? "text-gray-400" : "text-gray-500"
        }`}
      >
        {subtitle}
      </p>
    </button>
  );
}

function Field({
  label,
  children,
  isDark,
}: {
  label: string;
  children: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div>
      <label
        className={`block text-xs font-medium mb-1 ${
          isDark ? "text-gray-300" : "text-gray-600"
        }`}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
